<template>
  <div class="my-4 md:mx-5">
    <div class="mb-4 flex items-center justify-between border-b border-border-medium pb-2">
      <h1 class="text-h2">{{ t['w.wishlist.title']() }}</h1>
      <button class="btn btn-outline group flex items-center" @click="showShareLinkModal = true">
        <Icon class="mr-2 size-5 group-hover:invert group-focus:invert" name="link" />
        <span class="hidden md:inline">{{ t['w.wishlist.button.share']() }}</span>
        <span class="md:hidden">{{ t['w.wishlist.button.share_mobile']() }}</span>
      </button>
    </div>

    <div v-if="items.length > 0" class="grid grid-cols-[max-content_1fr] gap-x-5 pt-6 md:grid-cols-[max-content_1fr_max-content]">
      <WishlistItem v-for="(item, index) in items" :key="index" :item="item">
        <Button type="button" class="btn-icon btn-rounded btn-flat" icon-name="trash" @click="() => onProductRemoved(item)" />
      </WishlistItem>
    </div>
    <div v-else class="mx-auto mt-10 flex max-w-[570px] flex-col items-center gap-4 text-center">
      <img class="mx-auto max-w-[111px]" src="~/assets/illustrations/heart-house.svg" />
      <h2 class="mt-4 text-subtitle font-medium">{{ t['w.wishlist.empty_wishlist_title']() }}</h2>
      <div class="text-lg">{{ t['w.wishlist.empty_wishlist_text']() }}</div>
      <Icon class="size-9" name="heart" />
      <Button link="/" class="btn-rounded btn-lg" icon-name="arrow-right">{{ t['w.wishlist.start_searching_button']() }}</Button>
    </div>

    <ProductModal />
    <WishlistLinkModal v-model="showShareLinkModal" />
  </div>
</template>

<script setup lang="ts">
import Button from '~/components/button/Button.vue'
import Icon from '~/components/icon/Icon.vue'
import ProductModal from '~/components/product-modal/ProductModal.vue'
import WishlistItem from '~/components/account/WishlistItem.vue'
import WishlistLinkModal from '~/components/account/WishlistLinkModal.vue'
import type { WishlistRemoveRequest, WishlistRemoveResponse } from '~/types/on24-klient'
import type { WishlistItemProduct } from './wishlist-item-product'
import * as t from '~/messages'
import { useWishlistPageQuery } from '~/composables/queries/wishlist-page'

const { data, error, suspense } = await useWishlistPageQuery()
await suspense()
if (error.value) {
  throw createError(error.value)
}

const items = ref(data.value?.items ?? [])

const showShareLinkModal = ref(false)

const onProductRemoved = (product: WishlistItemProduct) => {
  const data: WishlistRemoveRequest = {
    productId: product.productId,
    variationId: null,
    optionIds: null,
  }

  $fetch<WishlistRemoveResponse>('/api/account/wishlist/remove', {
    method: 'POST',
    body: data,
  }).then((response) => {
    items.value = items.value.filter((item) => item.productId !== product.productId)
    useWishlistState().value = response.wishlistSize
  })
}

useHead({
  title: t['w.wishlist.title'](),
})
</script>
